// @flow

import React, { type Node, useState } from 'react';
import { GlobalStyle } from '@tectonics/commons/look-and-feel/uber/global-style';
import { Container } from '@tectonics/commons/look-and-feel/uber/atom/container';
import { HighlightBox } from '@tectonics/commons/look-and-feel/uber/atom/highlight-box';
import { Button } from '@tectonics/commons/look-and-feel/uber/atom/button';
import { Label } from '@tectonics/commons/look-and-feel/uber/atom/label';
import { InputField } from '@tectonics/commons/look-and-feel/uber/molecule/input-field';
import { InputBrazilianCpf } from '@tectonics/commons/look-and-feel/uber/molecule/input-brazilian-cpf';
import { InputCardCvv } from '@tectonics/commons/look-and-feel/uber/molecule/input-card-cvv';
import { InputCardDueDate } from '@tectonics/commons/look-and-feel/uber/molecule/input-card-due-date';
import { InputCardNumber } from '@tectonics/commons/look-and-feel/uber/molecule/input-card-number';
import { Footer } from '@tectonics/commons/look-and-feel/uber/organism/footer';
import { useVrAuthorizeSubmitHandlerWithStatus } from '../../../meal-voucher/vr/authorize-submit';
import {
  cardCvvErrorMessage,
  cardDueDateErrorMessage,
  cardHolderNameErrorMessage,
  cardNumberErrorMessage,
  userBrazilianCpfErrorMessage,
} from '../../../meal-voucher/vr/generic-validation-messages';
import { useVrUserInputValidations } from '../../../meal-voucher/vr/user-input-validations';
import {
  type VrUserInputValues,
  useVrUserInputValueChangeHandler,
} from '../../../meal-voucher/vr/user-input-values';
import {CardTokenizationError} from '../../../meal-voucher/vr/card-tokenization-error';

export function VrPage(): Node {
  const [vrUserInputValues, setVrUserInputValues] = useState<VrUserInputValues>({
    userBrazilianCpf: '',
    cardHolderName: '',
    cardNumber: '',
    cardDueDate: '',
    cardCvv: '',
  });

  const vrUserInputValidations = useVrUserInputValidations(vrUserInputValues);
  const handleVrUserInputValueChange = useVrUserInputValueChangeHandler(setVrUserInputValues);
  const [handleSubmit, formStatus, submitError] = useVrAuthorizeSubmitHandlerWithStatus(
    vrUserInputValues,
    vrUserInputValidations);

  return (
    <Container style={{ background: 'white' }}>
      <GlobalStyle />

      <form style={{ padding: '1em' }} onSubmit={handleSubmit}>
        <Label>Preencha os campos abaixo para registrar o cartão no <strong>Uber Eats</strong>:</Label>

        <HighlightBox style={{ marginTop: '1em' }}>

          <Line>
            <InputField
              label="CPF"
              placeholder="___.___.___-__"
              input={InputBrazilianCpf}
              value={vrUserInputValues.userBrazilianCpf}
              onChange={handleVrUserInputValueChange('userBrazilianCpf')}
              name="user_brazilian_cpf"
              error={formStatus === 'validated' ? userBrazilianCpfErrorMessage(vrUserInputValidations.userBrazilianCpf) : ''}
              disabled={formStatus === 'submitting'}
            />

          </Line>

          <Line>
            <InputField
              label="Nome do titular"
              placeholder="Como impresso no cartão"
              value={vrUserInputValues.cardHolderName}
              onChange={handleVrUserInputValueChange('cardHolderName')}
              error={formStatus === 'validated' ? cardHolderNameErrorMessage(vrUserInputValidations.cardHolderName) : ''}
              disabled={formStatus === 'submitting'}
            />
          </Line>

          <Line>
            <InputField
              label="Número do cartão"
              input={InputCardNumber}
              cardBrand="vr"
              value={vrUserInputValues.cardNumber}
              onChange={handleVrUserInputValueChange('cardNumber')}
              error={formStatus === 'validated' ? cardNumberErrorMessage(vrUserInputValidations.cardNumber) : ''}
              disabled={formStatus === 'submitting'}
            />
          </Line>

          <Line style={{ display: 'flex' }}>

            <div style={{ marginRight: '0.75em'}}>
              <InputField
                label="Validade do cartão"
                placeholder="__/__"
                input={InputCardDueDate}
                value={vrUserInputValues.cardDueDate}
                onChange={handleVrUserInputValueChange('cardDueDate')}
                error={formStatus === 'validated' ? cardDueDateErrorMessage(vrUserInputValidations.cardDueDate) : ''}
                disabled={formStatus === 'submitting'}
              />
            </div>

            <div>
              <InputField
                label="Código de segurança"
                input={InputCardCvv}
                value={vrUserInputValues.cardCvv}
                onChange={handleVrUserInputValueChange('cardCvv')}
                error={formStatus === 'validated' ? cardCvvErrorMessage(vrUserInputValidations.cardCvv) : ''}
                disabled={formStatus === 'submitting'}
              />
            </div>

          </Line>

        </HighlightBox>

        {formStatus === 'submitted' && submitError && (
          <Label kind="error" style={{ display: 'block', margin: '1em 0' }}>
            {parseSubmitError(submitError)}
          </Label>
        )}

        <Button style={{
          display: 'block',
          width: '100%',
          marginTop: '1em',
        }}>
          {formStatus === 'submitting' ? 'ENVIANDO...' : 'SALVAR CARTÃO'}
        </Button>
      </form>

      <Footer />

    </Container>
  );
}

function Line({ children, style }: LineProps): Node {
  return (
    <div style={{ ...style, margin: '1em' }}>
      {children}
    </div>
  );
}

type LineProps = {
  children: Node,
  style?: { [string]: string },
};

function parseSubmitError(submitError: Error): string {
  if (submitError instanceof CardTokenizationError) {
    return 'Dados de cartão inválidos';
  }

  return submitError.message;
}
